import styles from "./styles.module.scss";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import { Button, InputScale, RadioInput } from "components";
import { ProblemSolvingOptions, SocialOptions } from "./options";
import { useToast } from "hooks";

interface PersonalityData {
  social: string;
  socialRating: number;
  problemSolving: string;
  problemSolvingRating: number;
  innovation: number;
  drive: number;
  judge: number;
  othersNeeds: number;
  improvise: number;
  productive: number;
}

const initData: PersonalityData = {
  social: "",
  socialRating: -1,
  problemSolving: "",
  problemSolvingRating: -1,
  innovation: -1,
  drive: -1,
  judge: -1,
  othersNeeds: -1,
  improvise: -1,
  productive: -1,
};

const schema = yup
  .object()
  .shape({
    social: yup.string().required("Required"),
    socialRating: yup.number().min(0, "Rate your answer").required("Required"),
    problemSolving: yup.string().required("Required"),
    problemSolvingRating: yup
      .number()
      .min(0, "Rate your answer")
      .required("Required"),
    innovation: yup.number().min(0, "Rate your answer").required("Required"),
    drive: yup.number().min(0, "Rate your answer").required("Required"),
    judge: yup.number().min(0, "Rate your answer").required("Required"),
    othersNeeds: yup.number().min(0, "Rate your answer").required("Required"),
    improvise: yup.number().min(0, "Rate your answer").required("Required"),
    productive: yup.number().min(0, "Rate your answer").required("Required"),
  })
  .required();

interface AssessmentPersonalityProps {
  submit: (data: PersonalityData) => void;
}

const AssessmentPersonalityUI: React.FC<AssessmentPersonalityProps> = ({
  submit,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PersonalityData>({
    resolver: yupResolver(schema),
    defaultValues: initData,
  });

  const { openToast } = useToast();

  const onSubmit: SubmitHandler<PersonalityData> = (data) => submit(data);
  const onInvalid: SubmitErrorHandler<PersonalityData> = () => {
    openToast({
      heading: "Incomplete Form",
      text: "Some required fields are missing or contain errors. Please scroll up & review the highlighted fields.",
      type: "warning",
      show: true,
    });
  };

  return (
    <>
      <form>
        <RadioInput
          register={register}
          name={"social"}
          validatorMessage={
            errors.social?.message ?? errors.socialRating?.message
          }
          label="1. When you're in a social situation, which of the following do you prefer?"
          instruction="Select the most likely option and rate how operationally relevant it is to you."
          options={SocialOptions}
          type="radio"
          value={watch("social")}
          rating={{
            count: 7,
            startAt: 0,
            value: watch("socialRating"),
            onChange: (x) => setValue("socialRating", x),
          }}
        />
        <RadioInput
          register={register}
          name={"problemSolving"}
          validatorMessage={
            errors.problemSolving?.message ??
            errors.problemSolvingRating?.message
          }
          label="2. When it comes to problem-solving, which of the following approaches do you find most effective?"
          instruction="Select the most likely option and rate how operationally relevant it is to you."
          options={ProblemSolvingOptions}
          type="radio"
          value={watch("problemSolving")}
          rating={{
            count: 7,
            startAt: 0,
            value: watch("problemSolvingRating"),
            onChange: (x) => setValue("problemSolvingRating", x),
          }}
        />
        <InputScale
          register={register}
          name={"innovation"}
          validatorMessage={errors.innovation?.message}
          label="3. Creativity at the expense of practicality is foolishness. The best innovations are what people are familiar with. Do you agree with this rationale?"
          length={7}
          leftMost="Yes, I do"
          rightMost="I do not agree"
          startAt={6}
        />
        <InputScale
          register={register}
          name={"drive"}
          validatorMessage={errors.drive?.message}
          label="4. Which drives you more present realities or possibilities of the future?"
          length={7}
          leftMost="Present realities"
          rightMost="Future possibilities"
          startAt={6}
        />
        <InputScale
          register={register}
          name={"judge"}
          validatorMessage={errors.judge?.message}
          label="5. You are made judge of a dicey situation (with both facts and people’s lives at stake), in passing judgement which would ultimately guide your decision?"
          length={7}
          leftMost="Gut"
          rightMost="Facts"
          startAt={6}
        />
        <InputScale
          register={register}
          name={"othersNeeds"}
          validatorMessage={errors.othersNeeds?.message}
          label="6. Are you prone to putting others' needs ahead of yours?"
          length={7}
          leftMost="Yes, I do"
          rightMost="Only if it makes sense"
          startAt={6}
        />
        <InputScale
          register={register}
          name={"improvise"}
          validatorMessage={errors.improvise?.message}
          label="7. It’s easier for you to improvise than plan."
          length={7}
          leftMost="True, I prefer improvising"
          rightMost="False, I prefer to plan"
          startAt={6}
        />
        <InputScale
          register={register}
          name={"productive"}
          validatorMessage={errors.productive?.message}
          label="8. To have the most productive day, do you make progress on a bunch of tasks or thoroughly complete a few tasks?"
          length={7}
          leftMost="Make progress"
          rightMost="Thorough completion"
          startAt={6}
        />
        <Button
          className={styles.nextBtn}
          type="fill"
          onClick={handleSubmit(onSubmit, onInvalid)}
        >
          Next
        </Button>
      </form>
    </>
  );
};

export { AssessmentPersonalityUI };
